/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * typescript.ts - Utility functions for working with typescript features.
 */

/**
 * Often in a switch statement, typescript will not require you to add the default case if it can prove that your
 * switch was exhaustive. However, it doesn't afford that luxury for if-else statements.
 * 
 * This function takes a value of type `never`, meaning that it is only possible to write this function if typescript
 * thinks that there is no type left that this value can take. This means it should never be called. However,
 * there are ways of tricking typescript, so if this function ever is called, that means the developer has missed a
 * case - which is a logic error so we crash.
 * 
 * ```
 * type MyType = 'A' | 'B' | 'C';
 * 
 * function foo(x: MyType): string {
 *   if (x === 'A' || x === 'B') {
 *     return 'Was A or B';
 *   } else if (x === 'C') {
 *     return 'Was C';
 *   } else {
 *     unreachable(x);
 *   }
 * }
 * ```
 * 
 * @param x A value for which we've checked all its types
 */
export function unreachable(x: never): never {
  throw new Error(`Unreachable code was reached: ${x}; ${JSON.stringify(x)}`);
}

/**
 * In typescript, there's two common types of enum: numeric and string.
 * MyEnum.A is essentially just that number or string.
 * 
 * For numeric enums, you can get the key back like so:
 * 
 * ```
 * enum MyEnum {
 *   A = 1,
 *   B,
 * }
 * MyEnum[0] // 'A' 
 * MyEnum[MyEnum.A] // 'A' 
 * ```
 * 
 * For string enums, this is not possible. Instead, use this function to produce a map.
 * 
 * ```
 * enum MyEnum {
 *   A = 'a',
 *   B = 'b',
 * }
 * const MyEnumMap = reverseMappingForStringEnums(MyEnum);
 * MyEnumMap.get('a') // 'A' 
 * MyEnumMap.get(MyEnum.A) // 'A' 
 * ```
 * 
 * @param myEnum the Enum to produce a mapping for
 * @returns mapping from Enum value to key
 */
export function reverseMappingForStringEnums<T extends {[index:string]:string}>(myEnum: T): Map<string, keyof T> {
  return new Map(
    Object.entries(myEnum)
      .map(([key, value]: [keyof T, string]) => [value, key])
  );
}
